// ============================================================
// Inspection Defense™ — Individual / Facility
// Route: /inspection-defense
// V 10-Section Template: Name → Verdict → Who → What → Get → How →
//   System Context → Not For You If → Proof → CTA
// ISO: 2026-05-06
// ============================================================

import { Link } from "react-router-dom";

const SE = {
  cream:  "var(--se-cream, #f9f6f1)",
  black:  "var(--se-black, #1a1a1a)",
  gold:   "var(--se-gold, #d6b168)",
  soft:   "var(--se-soft-gray, #e6e2dc)",
  mid:    "var(--se-mid-gray, #b8b3ac)",
  deep:   "var(--se-deep-charcoal, #2a2a2a)",
  serif:  "var(--font-serif, 'Cormorant Garamond', Georgia, serif)",
  sans:   "var(--font-sans, 'Inter', system-ui, sans-serif)",
  mono:   "var(--font-mono, 'IBM Plex Mono', 'DM Mono', monospace)",
};

const TIERS = [
  {
    key:      "inspection_defense_individual",
    tier:     "OPERATOR",
    name:     "Inspection Defense™ — Operator",
    price:    "$197/yr",
    desc:     "Unlimited regulatory language analysis and Admin Review generation. Break any inspection finding into its logical structure. Built by a licensed Texas childcare operator with four active facilities.",
    items:    [
      "Unlimited inspection language analyses",
      "Admin Review document generation",
      "Obligation vs. recommendation classification",
      "Timeline and deadline extraction",
      "Appeal window identification",
      "Evidence-ready export format",
      "Full analysis history",
    ],
    cta:      "Activate Inspection Defense™ Operator",
    stripeKey: "inspection_defense_individual",
    featured: true,
  },
  {
    key:      "inspection_defense_facility",
    tier:     "FACILITY",
    name:     "Inspection Defense™ — Facility",
    price:    "$497/yr",
    desc:     "Up to 5 staff seats. Everything in Operator, plus shared admin view and team analysis history. One license for your entire facility.",
    items:    [
      "Up to 5 staff seats",
      "All Operator features for each seat",
      "Shared admin dashboard",
      "Team analysis history",
      "Coordinated inspection response",
      "Multi-facility coordination support",
    ],
    cta:      "Activate Inspection Defense™ Facility",
    stripeKey: "inspection_defense_facility",
  },
];

export default function InspectionDefense() {
  return (
    <div style={{ background: SE.cream, fontFamily: SE.sans, color: SE.black }}>

      {/* ── NAV ──────────────────────────────────────────────────────── */}
      <nav style={{
        background: SE.black, padding: "0 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: "52px", borderBottom: `1px solid ${SE.deep}`,
      }}>
        <Link to="/" style={{
          fontFamily: SE.mono, fontSize: "0.65rem", letterSpacing: "0.15em",
          color: SE.gold, textDecoration: "none", textTransform: "uppercase",
        }}>
          ← Catch The Pattern™
        </Link>
        <span style={{
          fontFamily: SE.mono, fontSize: "0.65rem", letterSpacing: "0.14em",
          color: SE.mid, textTransform: "uppercase",
        }}>
          Inspection Defense™
        </span>
        <Link to="/for-directors" style={{
          fontFamily: SE.sans, fontSize: "0.78rem", color: SE.mid,
          textDecoration: "none", letterSpacing: "0.03em",
        }}>
          Citation Calm™ →
        </Link>
      </nav>

      {/* ── §1: NAME + VERDICT ───────────────────────────────────────── */}
      <section style={{
        background: SE.black, color: SE.cream,
        padding: "88px 32px 72px", textAlign: "center",
      }}>
        <p style={{
          fontFamily: SE.mono, fontSize: "0.6rem", letterSpacing: "0.2em",
          color: SE.gold, textTransform: "uppercase", marginBottom: "16px",
        }}>
          AN ELLARI VENTURES SYSTEM — CARETIDE VERTICAL
        </p>
        <h1 style={{
          fontFamily: SE.serif, fontSize: "clamp(2.4rem, 5vw, 3.8rem)",
          fontWeight: 300, maxWidth: "760px", margin: "0 auto 20px", lineHeight: 1.15,
        }}>
          Inspection Defense™
        </h1>
        {/* VERDICT */}
        <p style={{
          fontFamily: SE.mono, fontSize: "0.75rem", color: SE.gold,
          letterSpacing: "0.12em", marginBottom: "20px", textTransform: "uppercase",
        }}>
          BREAK ANY INSPECTION FINDING INTO ITS LOGICAL STRUCTURE BEFORE YOU RESPOND.
        </p>
        <p style={{
          color: SE.mid, fontSize: "1rem", maxWidth: "580px",
          margin: "0 auto 40px", lineHeight: 1.75,
        }}>
          Inspection language is structured to support enforcement, not operator comprehension.
          Inspection Defense™ maps every finding's logical form — what the inspector documented,
          what it requires, and what you need to prove in response.
        </p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          <a href="#plans" style={{
            background: SE.gold, color: SE.black,
            padding: "14px 36px", fontFamily: SE.sans, fontWeight: 700,
            fontSize: "0.875rem", textDecoration: "none",
            borderRadius: "2px", letterSpacing: "0.05em",
          }}>
            Choose A Plan →
          </a>
          <a href="#what-it-does" style={{
            border: `1px solid ${SE.mid}`, color: SE.cream,
            padding: "14px 36px", fontFamily: SE.sans, fontSize: "0.875rem",
            textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
          }}>
            See How It Works
          </a>
        </div>
      </section>

      <Divider />

      {/* ── §2: WHO IT IS BUILT FOR ──────────────────────────────────── */}
      <section style={{ padding: "64px 32px", maxWidth: "900px", margin: "0 auto" }}>
        <Label label="WHO IT IS BUILT FOR" />
        <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 24px" }}>
          Licensed operators who need to understand what they are actually being cited for.
        </h2>
        <p style={{ fontSize: "0.92rem", color: SE.mid, lineHeight: 1.75, maxWidth: "700px" }}>
          Built by a licensed Texas childcare operator with four active facilities under
          HHSC oversight. Inspection Defense™ was developed to answer the question that
          follows every inspection: "What does this finding actually require of me —
          not as written in the standard, but as documented in this inspection report?"
        </p>
        <div style={{
          marginTop: "32px", display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: "16px",
        }}>
          {[
            "Licensed childcare center operators",
            "Licensed home childcare operators",
            "Multi-facility childcare program directors",
            "Compliance staff managing active inspection findings",
            "Operators preparing formal AR (Administrative Review) responses",
          ].map((who, i) => (
            <div key={i} style={{
              padding: "16px 18px", background: "#fff",
              border: `1px solid ${SE.soft}`, borderRadius: "4px",
              fontSize: "0.83rem", lineHeight: 1.5,
            }}>
              <span style={{ fontFamily: SE.mono, color: SE.gold, marginRight: "8px" }}>◆</span>
              {who}
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── §3: WHAT IT DOES ─────────────────────────────────────────── */}
      <section id="what-it-does" style={{ background: "#fff", padding: "64px 32px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <Label label="WHAT IT DOES" />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 36px" }}>
            Every inspection finding has a logical structure. Most operators only see the verdict.
          </h2>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "20px",
          }}>
            {[
              {
                step: "01",
                title: "Logical Structure Analysis",
                body: "Maps each finding's structure: what the inspector observed, what standard applies, what the stated violation is, and whether the standard-to-finding chain is well-formed or contains gaps.",
              },
              {
                step: "02",
                title: "Obligation Classification",
                body: "Determines whether a finding creates a mandatory corrective action, an advisory recommendation, or a documentation requirement. Each has a different response structure.",
              },
              {
                step: "03",
                title: "Evidence Gap Identification",
                body: "Identifies what documentation, practices, or evidence the inspector's finding implies — and what would be needed to contest or comply with each element.",
              },
              {
                step: "04",
                title: "Admin Review Generation",
                body: "Generates an Admin Review document in evidence-ready format. The formal operator-level response to inspection findings that goes back to licensing staff.",
              },
            ].map(card => (
              <div key={card.step} style={{
                padding: "24px", border: `1px solid ${SE.soft}`,
                borderTop: `3px solid ${SE.gold}`, borderRadius: "4px",
              }}>
                <div style={{
                  fontFamily: SE.mono, fontSize: "0.6rem",
                  color: SE.gold, marginBottom: "10px", letterSpacing: "0.14em",
                }}>
                  {card.step}
                </div>
                <div style={{ fontFamily: SE.serif, fontSize: "1rem", marginBottom: "10px" }}>
                  {card.title}
                </div>
                <div style={{ fontSize: "0.8rem", color: SE.mid, lineHeight: 1.65 }}>
                  {card.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ── §4: PLANS ────────────────────────────────────────────────── */}
      <section id="plans" style={{ padding: "72px 32px", maxWidth: "1100px", margin: "0 auto" }}>
        <Label label="PLANS" />
        <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 8px" }}>
          Choose Operator for individual use. Choose Facility for your entire team.
        </h2>
        <p style={{ color: SE.mid, fontSize: "0.88rem", marginBottom: "44px" }}>
          Both plans include unlimited analyses and Admin Review generation.
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "20px",
        }}>
          {TIERS.map(tier => (
            <div key={tier.key} style={{
              background: tier.featured ? SE.black : "#fff",
              border: `2px solid ${tier.featured ? SE.gold : SE.soft}`,
              borderRadius: "4px", padding: "32px",
              position: "relative",
            }}>
              {tier.featured && (
                <div style={{
                  position: "absolute", top: "16px", right: "16px",
                  background: SE.gold, color: SE.black,
                  fontFamily: SE.mono, fontSize: "0.56rem",
                  letterSpacing: "0.12em", padding: "4px 10px", textTransform: "uppercase",
                }}>
                  MOST COMMON
                </div>
              )}
              <div style={{
                fontFamily: SE.mono, fontSize: "0.58rem", color: SE.gold,
                letterSpacing: "0.16em", marginBottom: "10px", textTransform: "uppercase",
              }}>
                {tier.tier}
              </div>
              <div style={{
                fontFamily: SE.serif, fontSize: "1.1rem",
                color: tier.featured ? SE.cream : SE.black, marginBottom: "4px",
              }}>
                {tier.name}
              </div>
              <div style={{
                fontFamily: SE.mono, fontSize: "1.1rem",
                color: tier.featured ? SE.cream : SE.black, marginBottom: "12px",
              }}>
                {tier.price}
              </div>
              <p style={{
                fontSize: "0.82rem", lineHeight: 1.65,
                color: SE.mid, marginBottom: "24px",
              }}>
                {tier.desc}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: "8px" }}>
                {tier.items.map((item, i) => (
                  <li key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                    <span style={{ fontFamily: SE.mono, color: SE.gold, fontSize: "0.68rem", marginTop: "2px", flexShrink: 0 }}>◆</span>
                    <span style={{ fontSize: "0.79rem", color: SE.mid, lineHeight: 1.5 }}>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href={`https://shopellari.com/product/${tier.stripeKey}`}
                style={{
                  display: "block", textAlign: "center",
                  background: tier.featured ? SE.gold : SE.black,
                  color: tier.featured ? SE.black : SE.cream,
                  padding: "13px 20px", fontFamily: SE.sans, fontWeight: 700,
                  fontSize: "0.83rem", textDecoration: "none",
                  borderRadius: "2px", letterSpacing: "0.04em",
                }}
              >
                {tier.cta}
              </a>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── §5: SYSTEM CONTEXT ───────────────────────────────────────── */}
      <section style={{ background: "#fff", padding: "60px 32px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <Label label="SYSTEM CONTEXT" />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.3rem, 2.5vw, 1.8rem)", fontWeight: 400, margin: "16px 0 20px" }}>
            Inspection Defense™ is the operator layer. Citation Calm™ is the director layer.
          </h2>
          <p style={{ fontSize: "0.88rem", color: SE.mid, lineHeight: 1.75, maxWidth: "680px", marginBottom: "24px" }}>
            Inspection Defense™ provides structural analysis for licensed operators managing
            the full inspection response process. Citation Calm™ provides plain-language
            translation for directors managing individual citation records.
            Together, they cover the full licensed operator response chain.
          </p>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Link to="/for-directors" style={{
              background: SE.black, color: SE.cream,
              padding: "11px 24px", fontFamily: SE.sans, fontWeight: 600,
              fontSize: "0.82rem", textDecoration: "none",
              borderRadius: "2px", letterSpacing: "0.04em",
            }}>
              See Citation Calm™ →
            </Link>
            <a href="https://shopellari.com/stacks/record-it-stack" style={{
              border: `1px solid ${SE.soft}`, color: SE.mid,
              padding: "11px 24px", fontFamily: SE.sans, fontSize: "0.82rem",
              textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
            }}>
              The Record It Stack™ →
            </a>
          </div>
        </div>
      </section>

      <Divider />

      {/* ── §6: NOT FOR YOU IF ───────────────────────────────────────── */}
      <section style={{ padding: "60px 32px", maxWidth: "760px", margin: "0 auto" }}>
        <Label label="NOT FOR YOU IF" />
        <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {[
            "You are not in a regulated licensed environment",
            "Your inspections are not from a state or county licensing authority",
            "You want real-time legal advice — this is a language analysis tool, not legal counsel",
            "You want a personal pattern recognition training tool — see Catch The Pattern™ instead",
            "You only need director-level plain-language translation — see Citation Calm™ Starter at $97/yr",
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", gap: "12px", padding: "14px 16px",
              background: "#fff", border: `1px solid ${SE.soft}`,
              borderRadius: "4px",
            }}>
              <span style={{
                fontFamily: SE.mono, color: SE.mid,
                fontSize: "0.75rem", marginTop: "2px", flexShrink: 0,
              }}>×</span>
              <span style={{ fontSize: "0.83rem", color: SE.deep, lineHeight: 1.55 }}>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── §7: PROOF ────────────────────────────────────────────────── */}
      <section style={{ background: SE.black, color: SE.cream, padding: "60px 32px" }}>
        <div style={{ maxWidth: "760px", margin: "0 auto" }}>
          <Label label="PROOF" dark />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.3rem, 2.5vw, 1.8rem)", fontWeight: 400, margin: "16px 0 20px" }}>
            Built by a licensed operator. Refined on real findings.
          </h2>
          <p style={{ color: SE.mid, lineHeight: 1.75, marginBottom: "24px", fontSize: "0.88rem" }}>
            Inspection Defense™ was developed inside a licensed Texas childcare operation
            under active HHSC oversight. The analysis engine was trained on Texas minimum
            standards citations and AR response documentation — not hypothetical examples.
          </p>
          <div style={{
            padding: "20px 24px",
            border: `1px solid ${SE.deep}`, borderRadius: "4px",
            background: SE.deep,
          }}>
            <p style={{
              fontFamily: SE.serif, fontSize: "1.05rem", color: SE.cream,
              lineHeight: 1.6, margin: 0,
            }}>
              "Every finding has a structure. Most operators never see it because
              they are responding to the verdict, not the argument behind it.
              Inspection Defense™ surfaces the argument."
            </p>
            <p style={{ fontFamily: SE.mono, fontSize: "0.6rem", color: SE.gold, marginTop: "14px", letterSpacing: "0.1em" }}>
              ELLARI VENTURES — CARETIDE OPERATIONAL SERIES
            </p>
          </div>
        </div>
      </section>

      {/* ── §8: FINAL CTA ────────────────────────────────────────────── */}
      <section style={{ padding: "80px 32px", textAlign: "center", background: SE.cream }}>
        <p style={{
          fontFamily: SE.mono, fontSize: "0.62rem", letterSpacing: "0.18em",
          color: SE.gold, marginBottom: "16px", textTransform: "uppercase",
        }}>
          BEGIN
        </p>
        <h2 style={{
          fontFamily: SE.serif, fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
          fontWeight: 400, maxWidth: "560px", margin: "0 auto 12px",
        }}>
          The finding has a structure. Now you will see it.
        </h2>
        <p style={{ color: SE.mid, fontSize: "0.88rem", marginBottom: "32px" }}>
          Starting at $197/year for unlimited analyses and Admin Review generation.
        </p>
        <a href="#plans" style={{
          background: SE.black, color: SE.cream,
          padding: "16px 48px", fontFamily: SE.sans, fontWeight: 700,
          fontSize: "0.875rem", textDecoration: "none",
          borderRadius: "2px", letterSpacing: "0.06em",
          display: "inline-block",
        }}>
          Choose A Plan →
        </a>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────── */}
      <footer style={{
        background: SE.deep, padding: "24px 32px",
        display: "flex", justifyContent: "space-between",
        alignItems: "center", flexWrap: "wrap", gap: "12px",
      }}>
        <span style={{ fontFamily: SE.mono, fontSize: "0.6rem", color: SE.mid, letterSpacing: "0.1em" }}>
          INSPECTION DEFENSE™ — AN ELLARI VENTURES SYSTEM
        </span>
        <div style={{ display: "flex", gap: "20px" }}>
          {[
            { label: "Citation Calm™",      href: "/for-directors" },
            { label: "Catch The Pattern™",  href: "/" },
            { label: "ShopEllari",          href: "https://shopellari.com" },
          ].map(link => (
            <a key={link.label} href={link.href} style={{
              fontFamily: SE.sans, fontSize: "0.73rem",
              color: SE.mid, textDecoration: "none",
            }}>
              {link.label}
            </a>
          ))}
        </div>
      </footer>

    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function Label({ label, dark }: { label: string; dark?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div style={{ height: "1px", width: "20px", background: "var(--se-gold, #d6b168)" }} />
      <span style={{
        fontFamily: "var(--font-mono, monospace)", fontSize: "0.6rem",
        letterSpacing: "0.18em", color: "var(--se-gold, #d6b168)",
        textTransform: "uppercase",
      }}>
        {label}
      </span>
    </div>
  );
}

function Divider() {
  return <div style={{ height: "1px", background: "var(--se-gold, #d6b168)", opacity: 0.2, margin: "0 32px" }} />;
}
